import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faTerminal, faKey, faCopy, faCheckCircle, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { UiPageHeader, UiStatsGrid } from '../components/ui';
import type { StatCardDef } from '../components/ui';
import { ApiDocsProvider, useApiDocs } from '../components/apidocs';
import type { ApiVersion } from '../components/apidocs';
import { apiDocsStyles } from './apidocs/styles';
import { API_DOCS_TABS } from './apidocs/navigation';
import ApiDocsSidebar from './apidocs/ApiDocsSidebar';
import QuickReferenceFooter from './apidocs/QuickReferenceFooter';
import { usePlayground } from './apidocs/hooks/usePlayground';
import { useWebhookToolbox } from './apidocs/hooks/useWebhookToolbox';
import {
  OverviewSection, AuthenticationSection, OrdersSection, SlipOrderSection, PayoutSection,
  BalanceSection, BanksSection, SystemStatusSection, PlaygroundSection, WebhookToolboxSection,
  ErrorsSection, ExamplesSection, WebhooksSection,
} from './apidocs/sections';

/**
 * โครงหน้า API Docs — เนื้อหาทั้งหมดอยู่ใน ./apidocs
 *   ./apidocs/navigation.ts      เมนูฝั่งซ้าย
 *   ./apidocs/data/*             เนื้อหา (สเปค endpoint, ตัวอย่างโค้ด, ตาราง error, payload)
 *   ./apidocs/sections/*         1 แท็บ = 1 ไฟล์
 *   ../components/apidocs        component ที่ใช้ซ้ำ (CodeBlock, EndpointDoc, FieldsTable ฯลฯ)
 *
 * playground/webhook toolbox เก็บ state ไว้ระดับนี้ (ผ่าน hook) เพราะเป็นค่าที่ผู้ใช้พิมพ์เอง
 * ถ้าย้ายลงไปใน section ค่าจะหายทุกครั้งที่สลับแท็บ
 */
const ApiDocsContent: React.FC = () => {
  const { t } = useTranslation();
  const { apiKey, baseUrl, copyCode, copiedStates } = useApiDocs();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const playground = usePlayground();
  const webhookTools = useWebhookToolbox();

  // Scroll to top on tab change
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const infoCards: StatCardDef[] = [
    {
      icon: faTerminal,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'Base URL',
      value: (
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-sm font-mono text-gray-800 dark:text-slate-100 break-all">{baseUrl}</code>
          <button
            onClick={() => copyCode(baseUrl, 'card-base-url')}
            className="shrink-0 text-gray-400 hover:text-emerald-500 dark:text-slate-500 dark:hover:text-emerald-400 transition-colors"
            title="Copy"
          >
            <FontAwesomeIcon icon={copiedStates['card-base-url'] ? faCheckCircle : faCopy} className={copiedStates['card-base-url'] ? 'text-emerald-500' : ''} />
          </button>
        </div>
      ),
    },
    {
      icon: faKey,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'API Key',
      value: (
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-sm font-mono text-gray-800 dark:text-slate-100 break-all">{apiKey}</code>
          <button
            onClick={() => copyCode(apiKey, 'card-api-key')}
            className="shrink-0 text-gray-400 hover:text-amber-500 dark:text-slate-500 dark:hover:text-amber-400 transition-colors"
            title="Copy"
          >
            <FontAwesomeIcon icon={copiedStates['card-api-key'] ? faCheckCircle : faCopy} className={copiedStates['card-api-key'] ? 'text-emerald-500' : ''} />
          </button>
        </div>
      ),
    },
  ];

  const renderSection = () => {
    switch (activeTab) {
      case 'overview': return <OverviewSection />;
      case 'authentication': return <AuthenticationSection />;
      case 'endpoints': return <OrdersSection />;
      case 'slip-order': return <SlipOrderSection />;
      case 'payout': return <PayoutSection />;
      case 'balance': return <BalanceSection />;
      case 'banks': return <BanksSection />;
      case 'system-status': return <SystemStatusSection />;
      case 'playground': return <PlaygroundSection playground={playground} />;
      case 'webhook-tools': return <WebhookToolboxSection tools={webhookTools} />;
      case 'errors': return <ErrorsSection />;
      case 'examples': return <ExamplesSection />;
      case 'webhook': return <WebhooksSection />;
      default: return null;
    }
  };

  return (
    <>
      <style>{apiDocsStyles}</style>
      <div className="w-full min-h-screen">
        {/* ─────── Header ─────── */}
        <UiPageHeader
          icon={faRocket}
          title="API Documentation"
          subtitle={t('apiDocs.heroSubtitle')}
        />

        {/* ─────── Quick reference cards ─────── */}
        <UiStatsGrid cards={infoCards} cols={2} />

        {/* ─────── Mobile nav toggle ─────── */}
        <button
          className="lg:hidden mb-4 flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 font-medium text-sm"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
        >
          <FontAwesomeIcon icon={mobileNavOpen ? faTimes : faBars} />
          {mobileNavOpen ? 'Close Menu' : API_DOCS_TABS.find(tab => tab.id === activeTab)?.label || 'Menu'}
        </button>

        {/* ─────── Main Layout: Sidebar + Content ─────── */}
        <div className="flex gap-6 relative">
          <ApiDocsSidebar
            activeTab={activeTab}
            open={mobileNavOpen}
            onSelectTab={id => {
              setActiveTab(id);
              setMobileNavOpen(false);
            }}
          />

          {/* Mobile backdrop */}
          {mobileNavOpen && (
            <div className="fixed inset-0 bg-black/30 z-20 lg:hidden" onClick={() => setMobileNavOpen(false)} />
          )}

          {/* Content Area */}
          <main ref={contentRef} className="flex-1 min-w-0">
            <div className="api-docs-content-enter" key={activeTab}>
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200/60 dark:border-slate-700 p-5 md:p-7">
                {renderSection()}
              </div>
            </div>

            <QuickReferenceFooter />
          </main>
        </div>
      </div>
    </>
  );
};

const ApiDocsPage: React.FC = () => {
  const { merchant } = useAuth();
  const [apiVersion, setApiVersion] = useState<ApiVersion>('v2');
  const apiKey = merchant?.api_key || 'pk_your_api_key';

  return (
    <ApiDocsProvider apiKey={apiKey} apiVersion={apiVersion} onApiVersionChange={setApiVersion}>
      <ApiDocsContent />
    </ApiDocsProvider>
  );
};

export default ApiDocsPage;
